import type { Topic } from "@/types/film";

export const topics: Topic[] = [
  {
    id: "marriage",
    title: "婚姻与背叛：伦理的十字路口",
    subtitle: "当承诺遇上人性的裂缝",
    cover:
      "https://readdy.ai/api/search-image?query=Cinematic%20editorial%20banner%2C%20an%20empty%20wedding%20table%20with%20two%20cups%20and%20a%20withered%20bouquet%20by%20a%20window%2C%20moody%20deep%20blue%20shadows%20with%20warm%20amber%20light%2C%20dramatic%20atmosphere%2C%20subtle%20film%20grain%2C%20no%20text&width=1200&height=800&seq=topic-cover-01&orientation=landscape",
    intro:
      "婚姻是伦理题材里最经典的战场，因为它把最亲密的关系和最尖锐的冲突放在同一张餐桌上。这个专题挑选了五部从不同角度切入「承诺」的欧美影视作品，有的讲隐瞒，有的讲原谅，有的讲体面之下的裂缝。",
    reason:
      "我们想通过这些作品提醒你：婚姻里的伦理困境几乎没有标准答案，重要的不是谁对谁错，而是人在裂缝前如何选择成为更好的自己。",
    groups: [
      {
        title: "沉默与隐瞒",
        desc: "那些没说出口的部分，往往才是关系真正的重量。",
        filmIds: ["silent-boundary", "quiet-pact"],
      },
      {
        title: "承诺与抉择",
        desc: "在另一个人生面前，承诺究竟意味着什么。",
        filmIds: ["vows-in-the-rain", "forgotten-promise"],
      },
      {
        title: "控制与边界",
        desc: "以爱之名的靠近，何时变成了越界。",
        filmIds: ["fragile-love", "north-south-pact"],
      },
    ],
    relatedIds: ["silence", "city"],
  },
  {
    id: "silence",
    title: "沉默的代价：家庭伦理精选",
    subtitle: "不说，也是一种决定",
    cover:
      "https://readdy.ai/api/search-image?query=Cinematic%20editorial%20banner%2C%20a%20dim%20family%20living%20room%20at%20dusk%20with%20a%20single%20empty%20armchair%2C%20moody%20deep%20blue%20tones%20with%20warm%20amber%20glow%2C%20dramatic%20atmosphere%2C%20subtle%20film%20grain%2C%20no%20text&width=1200&height=800&seq=topic-cover-02&orientation=landscape",
    intro:
      "在家庭里，「不说」常常被当作一种保护。但如果沉默本身造成了伤害，它还算是善意的吗？这个专题聚焦家庭与代际关系中的沉默伦理，从老宅、病房到餐桌，追问那些被咽下去的话最终去了哪里。",
    reason:
      "如果你也曾在家庭里选择过沉默，或被迫接受过别人的沉默，这些作品会给你一种被理解的踏实感。",
    groups: [
      {
        title: "被掩埋的往事",
        desc: "真相迟到时，是否还来得及被原谅。",
        filmIds: ["empty-room", "choice-among-ashes"],
      },
      {
        title: "说不出口的爱",
        desc: "牺牲与隐瞒之间，往往只隔着一句话。",
        filmIds: ["quiet-pact", "borrowed-time"],
      },
      {
        title: "记忆与遗憾",
        desc: "当时间不再站在你这一边。",
        filmIds: ["forgotten-promise", "midnight-call"],
      },
    ],
    relatedIds: ["marriage", "court"],
  },
  {
    id: "court",
    title: "法律与良知：法庭伦理专题",
    subtitle: "在判词之外，还有人心",
    cover:
      "https://readdy.ai/api/search-image?query=Cinematic%20editorial%20banner%2C%20an%20empty%20wooden%20courtroom%20bench%20lit%20by%20a%20shaft%20of%20cold%20window%20light%2C%20moody%20deep%20blue%20tones%20with%20warm%20amber%20highlights%2C%20dramatic%20atmosphere%2C%20subtle%20film%20grain%2C%20no%20text&width=1200&height=800&seq=topic-cover-03&orientation=landscape",
    intro:
      "法庭是现代社会最正式的伦理实验室：它用规则去裁量人性，却也常常被人性反将一军。这个专题收录了六部把「法律」与「良心」放在同一被告席上的欧美影视作品。",
    reason:
      "法律给的是底线，良心给的是方向。这些作品会让你重新思考：当两者冲突时，我们凭什么做决定。",
    groups: [
      {
        title: "为谁辩护",
        desc: "当被辩护者正是你厌恶的人。",
        filmIds: ["testimony-day-night", "edge-of-sin"],
      },
      {
        title: "真相的版本",
        desc: "记忆、立场与证据之间的缝隙。",
        filmIds: ["second-truth", "judgment-by-the-sea"],
      },
      {
        title: "迟到的正义",
        desc: "翻案不仅需要勇气，还需要承受。",
        filmIds: ["last-witness", "before-dawn"],
      },
    ],
    relatedIds: ["silence", "city"],
  },
  {
    id: "city",
    title: "欲望之城：都市伦理群像",
    subtitle: "在光鲜的秩序里，藏好各自的裂缝",
    cover:
      "https://readdy.ai/api/search-image?query=Cinematic%20editorial%20banner%2C%20a%20rainy%20city%20street%20at%20night%20with%20blurred%20lights%20and%20a%20lone%20figure%20walking%20away%2C%20moody%20deep%20blue%20tones%20with%20warm%20amber%20bokeh%2C%20dramatic%20atmosphere%2C%20subtle%20film%20grain%2C%20no%20text&width=1200&height=800&seq=topic-cover-04&orientation=landscape",
    intro:
      "都市把无数互不相识的人挤在一起，也把无数伦理问题藏进了钢筋水泥的缝隙。这个专题聚焦都市语境下的身份、隐私与依赖，看看现代人如何在秩序里安放自己的越界。",
    reason:
      "都市伦理没有反派，只有一个个普通人在规则边缘试探。你会发现，屏幕里的困境离你其实很近。",
    groups: [
      {
        title: "身份的裂缝",
        desc: "同一个人，在不同场景里可以有多不一样。",
        filmIds: ["two-faced-mirror", "glass-house-secrets"],
      },
      {
        title: "亲密与边界",
        desc: "越靠近，越需要知道在哪里停下。",
        filmIds: ["fragile-love", "vows-in-the-rain"],
      },
      {
        title: "陌生人的善意",
        desc: "在最疏离的环境里，救赎也可能来自陌生人。",
        filmIds: ["midnight-call", "before-dawn"],
      },
    ],
    relatedIds: ["marriage", "court"],
  },
];